import React, { useState, useEffect } from 'react';
import { Layout, Card, Typography, InputNumber, Button, Dropdown, Space, message, Modal, Table, Switch, type MenuProps } from 'antd';
import { DownOutlined, SaveOutlined, EditOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { theme } from '../../theme';

const { Content } = Layout;
const { Title, Text } = Typography;

interface DesmoteProps {
  producto?: string;
}

interface DesmoteState {
  producto_id: number;
  producto: string;
  nivel: string; // Agregar nivel del producto
  balanza: {
    id: number;
    numero_ticket: string;
    cliente_id: number;
    cliente: string;
  };
  tickets: any[];
  ticketBalanza: string;
  cliente: string;
  cliente_id: number; // ID del cliente
  productor: string; // Nombre del productor
  productor_id: number; // ID del productor
  cantidadRollos: number;
}

interface FardoGrabado {
  id: number;
  numero_fardo: number;
  kilos: number;
  timestamp: Date;
  activo: boolean; // Nuevo campo para controlar si está activo o dado de baja
}

const Desmote: React.FC<DesmoteProps> = ({ producto = 'FIBRA' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, getAccessTokenSilently } = useAuth0();
  const [desmoteData, setDesmoteData] = useState<DesmoteState | null>(null);
  const [fardoNumero, setFardoNumero] = useState<number>(1);
  const [loadingFardo, setLoadingFardo] = useState<boolean>(false);
  const [fardosGrabados, setFardosGrabados] = useState<FardoGrabado[]>([]);
  const [nextId, setNextId] = useState<number>(1);
  const [taraProducto, setTaraProducto] = useState<number | null>(null);
  const [loadingTara, setLoadingTara] = useState<boolean>(false);
  const [modalResumenVisible, setModalResumenVisible] = useState<boolean>(false);
  const [modalDetalleVisible, setModalDetalleVisible] = useState<boolean>(false);
  const [modalFardoVisible, setModalFardoVisible] = useState<boolean>(false);
  const [editingFardo, setEditingFardo] = useState<number | null>(null);
  const [editingKilos, setEditingKilos] = useState<number | null>(null);
  const [nuevoNumeroFardo, setNuevoNumeroFardo] = useState<number | null>(1);
  
  // Obtener resolución de pantalla
  const [screenResolution, setScreenResolution] = useState<string>('');
  
  // Log cuando cambia el número de fardo
  useEffect(() => {
    console.log('Fardo número actualizado:', fardoNumero);
  }, [fardoNumero]);
  const [kilos, setKilos] = useState<number | null>(null);
  const [restoNegativo, setRestoNegativo] = useState<number | null>(null);
  const [restoPositivo, setRestoPositivo] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState<number>(1);
  const [modulo, setModulo] = useState<number>(1);
  const [cliente, setCliente] = useState<string>('CLIENTE');
  
  // Estado para el coeficiente de semilla
  const [coeficienteSemilla, setCoeficienteSemilla] = useState<number>(1.4);

  // Recibir datos del estado de navegación y obtener último fardo
  useEffect(() => {
    if (location.state) {
      const stateData = location.state as DesmoteState;
      setDesmoteData(stateData);
      console.log('Datos recibidos en Desmote:', stateData);
      console.log('Ticket:', stateData.ticketBalanza);
      console.log('Cantidad de rollos:', stateData.cantidadRollos);
      console.log('Cliente:', stateData.cliente);
      console.log('Productor:', stateData.productor);
      console.log('Productor ID:', stateData.productor_id);
      
      // Obtener el último fardo desmotado
      console.log('Llamando a obtenerUltimoFardo con:', stateData.producto_id, stateData.producto);
      obtenerUltimoFardo(stateData.producto_id, stateData.producto, stateData.cliente_id);
      
      // Obtener la tara del producto
      console.log('Llamando a obtenerTaraProducto con:', stateData.producto_id);
      obtenerTaraProducto(stateData.producto_id);
    } else {
      console.log('No hay datos de navegación, usando valores por defecto');
    }
  }, [location.state, user]); // Agregar user como dependencia

  // Cargar coeficiente de semilla desde la API
  useEffect(() => {
    const cargarCoeficienteSemilla = async () => {
      if (!user?.id_empresa || !user?.id_sucursal) {
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        
        const response = await fetch(
          `${process.env.REACT_APP_RELACION_URL}/leer_datos_fijos/${user.id_empresa}/${user.id_sucursal}/SEM`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const result = await response.json();
        
        if (result && result.length > 0 && result[0].valor_numerico) {
          // Convertir el valor numérico de centésimas a decimal
          setCoeficienteSemilla(result[0].valor_numerico / 100);
          console.log('Coeficiente de semilla cargado:', result[0].valor_numerico / 100);
        } else {
          console.log('No se encontró coeficiente de semilla, usando valor por defecto 1.4');
          setCoeficienteSemilla(1.4);
        }
      } catch (error) {
        console.error('Error al cargar coeficiente de semilla:', error);
        setCoeficienteSemilla(1.4); // Valor por defecto
      }
    };

    cargarCoeficienteSemilla();
  }, [getAccessTokenSilently, user?.id_empresa, user?.id_sucursal]);

  // Función para obtener el último fardo desmotado
  const obtenerUltimoFardo = async (productoId: number, productoNombre: string, clienteId: number) => {
    console.log('Iniciando obtenerUltimoFardo con productoId:', productoId);
    setLoadingFardo(true);
    try {
      const token = await getAccessTokenSilently();
      const empresaId = user?.id_empresa;
      const sucursalId = user?.id_sucursal;

      console.log('Datos de usuario:', { empresaId, sucursalId, productoId });

      if (!empresaId || !sucursalId) {
        console.error('No se pudo obtener empresaId o sucursalId');
        return;
      }

      const url = `${process.env.REACT_APP_PRODUCCION_URL}/ultimo_fardo_desmotado/${empresaId}/${sucursalId}/${productoId}/${clienteId}`;
      console.log('URL de la petición:', url);

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      console.log('Respuesta último fardo:', data);

      if (data.status === 'success' && data.ultimo_fardo !== null && data.ultimo_fardo !== undefined) {
        // Incrementar en 1 el último fardo para el siguiente
        const nuevoFardo = data.ultimo_fardo + 1;
        console.log('Estableciendo fardo número:', nuevoFardo);
        setFardoNumero(nuevoFardo);
        
        // Verificar que se estableció correctamente
        setTimeout(() => {
          console.log('Verificación - Fardo número actual después de setFardoNumero:', fardoNumero);
        }, 100);
      } else {
        // Si no hay datos, empezar desde 1
        console.log('No hay datos de último fardo, estableciendo 1');
        setFardoNumero(1);
      }
    } catch (error) {
      console.error('Error al obtener último fardo:', error);
      // En caso de error, mantener el valor por defecto
      setFardoNumero(1);
    } finally {
      setLoadingFardo(false);
    }
  };

  // Función para obtener la tara del producto
  const obtenerTaraProducto = async (productoId: number) => {
    console.log('Iniciando obtenerTaraProducto con productoId:', productoId);
    setLoadingTara(true);
    try {
      const token = await getAccessTokenSilently();
      const empresaId = user?.id_empresa;
      const sucursalId = user?.id_sucursal;

      console.log('Datos de usuario para tara:', { empresaId, sucursalId, productoId });

      if (!empresaId || !sucursalId) {
        console.error('No se pudo obtener empresaId o sucursalId para tara');
        return;
      }

      const url = `${process.env.REACT_APP_RELACION_URL}/tara_fardo/${empresaId}/${sucursalId}/${productoId}`;
      console.log('URL de la petición tara:', url);

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      console.log('Respuesta tara del producto:', data);

      if (data && Array.isArray(data) && data.length > 0 && data[0].tara) {
        const taraValue = parseFloat(data[0].tara);
        console.log('Estableciendo tara del producto:', taraValue);
        setTaraProducto(taraValue);
      } else {
        console.log('No hay datos de tara, estableciendo null');
        setTaraProducto(null);
      }
    } catch (error) {
      console.error('Error al obtener tara del producto:', error);
      setTaraProducto(null);
    } finally {
      setLoadingTara(false);
    }
    };

  React.useEffect(() => {
    const updateResolution = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setScreenResolution(`${width}x${height}`);
    };

    updateResolution();
    window.addEventListener('resize', updateResolution);

    return () => window.removeEventListener('resize', updateResolution);
  }, []);

  
  // Función para calcular el resumen
  const calcularResumen = () => {
    const fardosActivos = fardosGrabados.filter(fardo => fardo.activo);
    const cantidad = fardosActivos.length;
    const kilosBrutos = fardosActivos.reduce((total, fardo) => total + fardo.kilos, 0);
    const kilosTara = taraProducto ? taraProducto * cantidad : 0;
    const kilosNetos = kilosBrutos - kilosTara;
    
    // Calcular kilos_semilla: mismo criterio que el backend -> solo producto FIBRA
    let kilosSemilla = 0;
    if (desmoteData?.producto === 'FIBRA') {
      kilosSemilla = kilosNetos * coeficienteSemilla;
    }
    
    return {
      cantidad,
      kilosBrutos: kilosBrutos.toFixed(2),
      kilosTara: kilosTara.toFixed(2),
      kilosNetos: kilosNetos.toFixed(2),
      kilosSemilla: kilosSemilla.toFixed(2)
    };
  };

  // Estado para el modal de tickets/rollos
  const [modalTicketsRollosVisible, setModalTicketsRollosVisible] = useState<boolean>(false);

  // Configuración del menú desplegable
  const menuItems: MenuProps['items'] = [
    {
      key: 'tickets_rollos',
      label: 'Ticket/Rollos',
      onClick: () => {
        setModalTicketsRollosVisible(true);
      },
    },
    {
      key: 'resumen',
      label: 'Resumen',
      onClick: () => {
        setModalResumenVisible(true);
      },
    },
    {
      key: 'detalle',
      label: 'Detalle',
      onClick: () => {
        setModalDetalleVisible(true);
      },
    },
    {
      key: 'numero_fardo',
      label: 'Nº Fardo',
      onClick: () => {
        abrirModalFardo();
      },
    },
  ];

  const handleGrabar = async () => {
    if (!kilos || kilos <= 0) {
      message.error('Por favor ingrese un valor válido para los kilos');
      return;
    }

    setLoading(true);
    try {
             // Crear nuevo fardo grabado
       const nuevoFardo: FardoGrabado = {
         id: nextId,
         numero_fardo: fardoNumero,
         kilos,
         timestamp: new Date(),
         activo: true
       };

      // Agregar al array de fardos grabados
      setFardosGrabados(prev => [...prev, nuevoFardo]);
      
      // Incrementar el siguiente ID
      setNextId(prev => prev + 1);

      console.log('Fardo grabado:', nuevoFardo);
      console.log('Array completo de fardos:', [...fardosGrabados, nuevoFardo]);

      // Simular llamada a API
      await new Promise(resolve => setTimeout(resolve, 1000));

      message.success(`Fardo ${fardoNumero} grabado exitosamente con ${kilos} kg`);
      
      // Limpiar formulario y preparar para el siguiente fardo
      setKilos(null);
      setFardoNumero(prev => prev + 1);
    } catch (error) {
      message.error('Error al grabar los datos');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

    const handleGrabarTodo = async () => {
    // Verificar que hay fardos para grabar
    if (fardosGrabados.length === 0) {
      message.error('No hay fardos para grabar');
      return;
    }

    setLoading(true);
    try {
      // Preparar datos para enviar al backend
      const token = await getAccessTokenSilently();
      const empresaId = user?.id_empresa;
      const sucursalId = user?.id_sucursal;

      if (!empresaId || !sucursalId) {
        message.error('No se pudo obtener los datos de empresa o sucursal');
        return;
      }

      // Obtener solo los fardos activos (sin agregar el fardo actual)
      const fardosActivos = fardosGrabados.filter(fardo => fardo.activo);

             // Calcular kilos_semilla: mismo criterio que el backend -> solo producto FIBRA
       let kilosSemilla = 0; // Valor por defecto
       if (desmoteData?.producto === 'FIBRA') {
         const fardosActivos = fardosGrabados.filter(fardo => fardo.activo);
         const kilosBrutos = fardosActivos.reduce((total, fardo) => total + fardo.kilos, 0);
         const kilosTara = taraProducto ? taraProducto * fardosActivos.length : 0;
         const kilosNetos = kilosBrutos - kilosTara;
         kilosSemilla = kilosNetos * coeficienteSemilla;
       }

              // Obtener fecha/hora inicio y final del array de fardos
       const fechaHoraInicio = fardosActivos[0].timestamp.toISOString().slice(0, 19).replace('T', ' ');
       const fechaHoraFinal = fardosActivos[fardosActivos.length - 1].timestamp.toISOString().slice(0, 19).replace('T', ' ');

       // Estructura de datos a enviar (mismo formato que ProduccionLayout.tsx)
       const dataToSend = {
         produccion: {
           cliente_id: desmoteData?.cliente_id || desmoteData?.balanza?.cliente_id || 1,
           productor_id: desmoteData?.productor_id || 1,
           producto_id: desmoteData?.producto_id,
           kilos_semilla: kilosSemilla,
           fecha_hora_inicio: fechaHoraInicio,
           fecha_hora_final: fechaHoraFinal,
           resto_positivo: restoPositivo || 0,
           resto_negativo: restoNegativo || 0,
           tipo_registracion: "A" // Automática
         },
         ticket: desmoteData?.tickets?.map(ticket => ({
           balanza_id: ticket.balanza_id || 1, // Usar balanza_id real de la API
           numero_ticket: ticket.numero_ticket,
           rollos: ticket.rollo_identificacion ? [{
             id_rollo: ticket.rollo_id || 1, // Usar rollo_id real de la API
             rollo_identificacion: ticket.rollo_identificacion
           }] : []
         })) || [{
           balanza_id: desmoteData?.balanza?.id || 1,
           numero_ticket: desmoteData?.balanza?.numero_ticket || '1',
           rollos: []
         }],
         fardo: fardosActivos.map(fardo => ({
           nro_fardo: fardo.numero_fardo,
           kilos_fardo: fardo.kilos,
           kilos_tara: taraProducto || 0,
           fecha_hora: fardo.timestamp.toISOString().slice(0, 19).replace('T', ' ')
         }))
       };

             console.log('Datos a enviar al backend:', dataToSend);
       console.log('Datos del cliente y productor en la producción:', {
         cliente: desmoteData?.cliente,
         cliente_id: desmoteData?.cliente_id,
         cliente_id_enviado: dataToSend.produccion.cliente_id,
         productor: desmoteData?.productor,
         productor_id: desmoteData?.productor_id,
         productor_id_enviado: dataToSend.produccion.productor_id
       });
       
       // Log detallado de los datos de tickets y rollos
       console.log('=== VERIFICACIÓN DATOS DE TICKETS Y ROLLOS ===');
       if (desmoteData?.tickets) {
         desmoteData.tickets.forEach((ticket, index) => {
           console.log(`Ticket ${index + 1}:`, {
             numero_ticket: ticket.numero_ticket,
             balanza_id_original: ticket.balanza_id,
             rollo_id_original: ticket.rollo_id,
             rollo_identificacion: ticket.rollo_identificacion,
             id_interno: ticket.id
           });
         });
       }
       console.log('=== FIN VERIFICACIÓN ===');

       // Enviar datos al backend
       const url = `${process.env.REACT_APP_PRODUCCION_URL}/alta_produccion/${empresaId}/${sucursalId}`;
       
       const response = await fetch(url, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           'Authorization': `Bearer ${token}`,
         },
         body: JSON.stringify(dataToSend)
       });

      const responseData = await response.json();
      console.log('Respuesta del backend:', responseData);
      if (!response.ok) {
        console.error('Error backend - mensaje:', responseData.message);
        console.error('Error backend - detalles:', JSON.stringify(responseData.detalles || responseData.error || {}, null, 2));
      }

             if (response.ok && responseData.status === 'success') {
         message.success(`Producción grabada exitosamente. ${responseData.fardos_creados} fardos creados.`);
         
         // Limpiar formulario y preparar para el siguiente fardo
         setKilos(null);
         setRestoNegativo(null);
         setRestoPositivo(null);
         setFardoNumero(prev => prev + 1);
         
         // Limpiar el array de fardos grabados después de grabar exitosamente
         setFardosGrabados([]);
         setNextId(1);
         
         // Navegar a previoDesmote después de grabar exitosamente
         setTimeout(() => {
           navigate('/previoDesmote');
         }, 1500); // Esperar 1.5 segundos para que el usuario vea el mensaje de éxito
       } else {
         message.error(`Error al grabar producción: ${responseData.message || 'Error desconocido'}`);
       }
    } catch (error) {
      message.error('Error al grabar todos los datos');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para modificar un fardo existente
  const modificarFardo = (id: number, nuevosKilos: number) => {
    setFardosGrabados(prev => 
      prev.map(fardo => 
        fardo.id === id 
          ? { ...fardo, kilos: nuevosKilos, timestamp: new Date() }
          : fardo
      )
    );
    message.success(`Fardo ${fardosGrabados.find(f => f.id === id)?.numero_fardo} modificado a ${nuevosKilos} kg`);
  };

  // Función para cambiar el estado de un fardo (activar/desactivar)
  const handleEstadoChange = (id: number, checked: boolean) => {
    const fardo = fardosGrabados.find(f => f.id === id);
    setFardosGrabados(prev => 
      prev.map(fardo => 
        fardo.id === id 
          ? { ...fardo, activo: checked, timestamp: new Date() }
          : fardo
      )
    );
    message.success(
      checked 
        ? `Fardo ${fardo?.numero_fardo} reactivado` 
        : `Fardo ${fardo?.numero_fardo} dado de baja`
    );
  };

  // Función para iniciar edición de un fardo
  const iniciarEdicion = (fardo: FardoGrabado) => {
    setEditingFardo(fardo.id);
    setEditingKilos(fardo.kilos);
  };

  // Función para guardar la edición de un fardo
  const guardarEdicion = () => {
    if (editingFardo && editingKilos !== null && editingKilos > 0) {
      modificarFardo(editingFardo, editingKilos);
      setEditingFardo(null);
      setEditingKilos(null);
    } else {
      message.error('Por favor ingrese un valor válido para los kilos');
    }
  };

  // Función para cancelar la edición
  const cancelarEdicion = () => {
    setEditingFardo(null);
    setEditingKilos(null);
  };

  // Función para abrir modal de cambio de número de fardo
  const abrirModalFardo = () => {
    setNuevoNumeroFardo(fardoNumero);
    setModalFardoVisible(true);
  };

  // Función para cambiar el número de fardo
  const cambiarNumeroFardo = () => {
    if (nuevoNumeroFardo !== null && nuevoNumeroFardo > 0) {
      setFardoNumero(nuevoNumeroFardo);
      setModalFardoVisible(false);
      message.success(`Número de fardo cambiado a: ${nuevoNumeroFardo}`);
    } else {
      message.error('Por favor ingrese un número de fardo válido');
    }
  };

  // Configuración de columnas para la tabla de fardos
  const columns = [
    {
      title: 'Fardo Nº',
      dataIndex: 'numero_fardo',
      key: 'numero_fardo',
      width: 100,
      render: (text: number, record: FardoGrabado) => (
        <Text 
          strong 
          style={{ 
            fontSize: '14px',
            color: record.activo ? '#262626' : '#999',
            textDecoration: record.activo ? 'none' : 'line-through'
          }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: 'Kilos',
      dataIndex: 'kilos',
      key: 'kilos',
      width: 120,
      render: (text: number, record: FardoGrabado) => {
        if (editingFardo === record.id) {
          return (
            <InputNumber
              value={editingKilos}
              onChange={(value) => setEditingKilos(value)}
              style={{ width: '80px' }}
              min={0}
              step={0.01}
              precision={2}
            />
          );
        }
        return (
          <Text 
            style={{ 
              fontSize: '14px',
              color: record.activo ? '#262626' : '#999'
            }}
          >
            {text.toFixed(2)} kg
          </Text>
        );
      },
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 80,
      render: (text: string, record: FardoGrabado) => (
        <Text 
          style={{ 
            fontSize: '12px',
            color: record.activo ? '#52c41a' : '#ff4d4f',
            fontWeight: 'bold'
          }}
        >
          {record.activo ? 'Activo' : 'Baja'}
        </Text>
      ),
    },
    {
      title: 'Fecha/Hora',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 150,
      render: (timestamp: Date, record: FardoGrabado) => (
        <Text style={{ 
          fontSize: '12px', 
          color: record.activo ? '#666' : '#999'
        }}>
          {new Date(timestamp).toLocaleString('es-ES')}
        </Text>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 140,
      render: (text: string, record: FardoGrabado) => {
        if (editingFardo === record.id) {
          return (
            <Space size="small">
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={guardarEdicion}
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              />
              <Button
                size="small"
                onClick={cancelarEdicion}
              >
                Cancelar
              </Button>
            </Space>
          );
        }
        return (
          <Space size="small">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => iniciarEdicion(record)}
              style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
              disabled={!record.activo}
            />
            <Switch
              checked={record.activo}
              onChange={(checked) => handleEstadoChange(record.id, checked)}
              checkedChildren="Activo"
              unCheckedChildren="Baja"
              size="small"
            />
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '0' }}>
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '22px', 
        fontWeight: 600, 
        marginBottom: '12px' 
      }}>
        Desmote [{desmoteData?.producto || 'FIBRA'}] 
      </h1>

      <Card
        style={{
          maxWidth: '1000px',
          margin: '0 auto',
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '13px',
            fontWeight: 600,
            position: 'relative',
            padding: '8px 16px'
          }
        }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Desmote [{desmoteData?.producto || 'FIBRA'}]</span>
            <Dropdown
              menu={{ items: menuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button 
                type="text"
                icon={<DownOutlined />}
                style={{
                  color: 'white',
                  border: 'none',
                  padding: '0',
                  height: 'auto'
                }}
              />
            </Dropdown>
          </div>
        }
      >
      <div style={{ padding: '12px' }}>
        {/* Subtítulo */}
        <div style={{
          textAlign: 'center',
          marginBottom: '12px',
          padding: '8px',
          backgroundColor: theme.colors.backgroundGray,
          borderRadius: '8px'
        }}>
          <Text style={{
            color: theme.colors.textPrimary,
            fontSize: '13px',
            fontWeight: 500
          }}>
            Ticket [{desmoteData?.ticketBalanza || desmoteData?.balanza?.numero_ticket || ticket}] / Rollos [{desmoteData?.cantidadRollos || 0}] / [{desmoteData?.cliente || desmoteData?.balanza?.cliente || cliente}]
          </Text>
        </div>

        {/* Formulario */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '12px',
          marginBottom: '16px'
        }}>
          {/* Fardo Nº */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontWeight: 600,
              color: theme.colors.textPrimary,
              fontSize: '12px',
              textAlign: 'center'
            }}>
              Fardo Nº
            </label>
            <div style={{
              textAlign: 'center',
              padding: '8px',
              backgroundColor: theme.colors.backgroundGray,
              borderRadius: '8px',
              border: `1px solid ${theme.colors.borderLight}`
            }}>
              <Text style={{ 
                fontSize: '18px',
                color: theme.colors.primary,
                fontWeight: 'bold'
              }}>
                {loadingFardo ? 'Cargando...' : fardoNumero}
              </Text>
            </div>
          </div>

          {/* Kilos */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontWeight: 600,
              color: theme.colors.textPrimary,
              fontSize: '12px'
            }}>
              Kilos <span style={{ color: theme.colors.error }}>*</span>
            </label>
            <InputNumber
              value={kilos}
              onChange={(value) => setKilos(value)}
              size="middle"
              style={{ width: '100%', fontSize: '13px' }}
              min={0}
              step={0.01}
              placeholder="Ingrese los kilos"
              precision={2}
            />
          </div>
        </div>

        {/* Botón Grabar */}
        <div style={{ marginBottom: '16px' }}>
          <Button
            type="primary"
            size="middle"
            onClick={handleGrabar}
            loading={loading}
            style={{
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.secondary,
              fontWeight: 500,
              width: '100%',
              height: '32px',
              fontSize: '13px'
            }}
          >
            Grabar
          </Button>
        </div>

        {/* Resto (-) y Resto (+) */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '4px', 
            fontWeight: 600,
            color: theme.colors.textPrimary,
            fontSize: '12px'
          }}>
            Resto
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '4px',
                fontSize: '12px',
                color: theme.colors.textSecondary
              }}>
                Resto (-)
              </label>
              <InputNumber
                value={restoNegativo}
                onChange={(value) => setRestoNegativo(value)}
                size="middle"
                style={{ width: '100%', fontSize: '13px' }}
                min={0}
                step={0.01}
                placeholder="0.00"
                precision={2}
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '4px',
                fontSize: '12px',
                color: theme.colors.textSecondary
              }}>
                Resto (+)
              </label>
              <InputNumber
                value={restoPositivo}
                onChange={(value) => setRestoPositivo(value)}
                size="middle"
                style={{ width: '100%', fontSize: '13px' }}
                min={0}
                step={0.01}
                placeholder="0.00"
                precision={2}
              />
            </div>
          </div>
        </div>

        {/* Información de Tara del Producto */}
        <div style={{
          textAlign: 'center',
          marginBottom: '16px',
          padding: '10px',
          backgroundColor: theme.colors.backgroundGray,
          borderRadius: '8px',
          border: `1px solid ${theme.colors.borderLight}`
        }}>
          <Text style={{
            color: theme.colors.textPrimary,
            fontSize: '12px',
            fontWeight: 500
          }}>
            {loadingTara ? 'Cargando tara...' : 
             taraProducto !== null ? `Tara en kilos: ${taraProducto}` : 
             'Tara no disponible'}
          </Text>
        </div>

        {/* Botón GrabarTodo */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: '8px',
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: `1px solid ${theme.colors.borderLight}`
        }}>
          <Button
            type="primary"
            size="middle"
            onClick={handleGrabarTodo}
            loading={loading}
            disabled={fardosGrabados.length === 0}
            style={{
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.secondary,
              fontWeight: 500,
              minWidth: '180px',
              height: '32px',
              fontSize: '13px',
              opacity: fardosGrabados.length === 0 ? 0.6 : 1
            }}
          >
            Grabar Todo {fardosGrabados.length > 0 && `(${fardosGrabados.length} fardos)`}
          </Button>
        </div>

                {/* Modal de Resumen */}
                <Modal
                  title={`Resumen [${desmoteData?.producto || 'FIBRA'}]`}
                  open={modalResumenVisible}
                  onCancel={() => setModalResumenVisible(false)}
                  footer={[
                    <Button key="cerrar" onClick={() => setModalResumenVisible(false)}>
                      Cerrar
                    </Button>
                  ]}
                  width={500}
                >
                  <div style={{ padding: '20px 0' }}>
                    {(() => {
                      const resumen = calcularResumen();
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                                     <div style={{ 
                             display: 'flex', 
                             justifyContent: 'space-between', 
                             alignItems: 'center',
                             padding: '12px',
                             backgroundColor: '#f8f9fa',
                             borderRadius: '8px',
                             border: '1px solid #e9ecef'
                           }}>
                             <Text strong style={{ fontSize: '16px' }}>Cantidad:</Text>
                             <Text style={{ fontSize: '16px', color: '#000000' }}>{resumen.cantidad} fardos</Text>
                           </div>
                           
                           <div style={{ 
                             display: 'flex', 
                             justifyContent: 'space-between', 
                             alignItems: 'center',
                             padding: '12px',
                             backgroundColor: '#f8f9fa',
                             borderRadius: '8px',
                             border: '1px solid #e9ecef'
                           }}>
                             <Text strong style={{ fontSize: '16px' }}>Kilos Brutos:</Text>
                             <Text style={{ fontSize: '16px', color: '#000000' }}>{resumen.kilosBrutos} kg</Text>
                           </div>
                           
                           <div style={{ 
                             display: 'flex', 
                             justifyContent: 'space-between', 
                             alignItems: 'center',
                             padding: '12px',
                             backgroundColor: '#f8f9fa',
                             borderRadius: '8px',
                             border: '1px solid #e9ecef'
                           }}>
                             <Text strong style={{ fontSize: '16px' }}>Kilos Tara:</Text>
                             <Text style={{ fontSize: '16px', color: '#000000' }}>{resumen.kilosTara} kg</Text>
                           </div>
                           
                                                       <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              padding: '12px',
                              backgroundColor: '#e6f7ff',
                              borderRadius: '8px',
                              border: '2px solid #1890ff'
                            }}>
                              <Text strong style={{ fontSize: '18px' }}>Kilos Netos:</Text>
                              <Text style={{ fontSize: '18px', color: '#000000', fontWeight: 'bold' }}>{resumen.kilosNetos} kg</Text>
                            </div>
                            
                            {/* Mostrar Kilos Semilla solo para productos FIBRA */}
                            {desmoteData?.producto === 'FIBRA' && (
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                padding: '12px',
                                backgroundColor: '#fff7e6',
                                borderRadius: '8px',
                                border: '2px solid #fa8c16'
                              }}>
                                <Text strong style={{ fontSize: '16px' }}>Kilos Semilla (×{coeficienteSemilla}):</Text>
                                <Text style={{ fontSize: '16px', color: '#000000', fontWeight: 'bold' }}>{resumen.kilosSemilla} kg</Text>
                              </div>
                            )}
                            

                        </div>
                      );
                    })()}
                  </div>
                </Modal>

                {/* Modal de Detalle */}
                <Modal
                  title={`Detalle de Fardos [${desmoteData?.producto || 'FIBRA'}]`}
                  open={modalDetalleVisible}
                  onCancel={() => setModalDetalleVisible(false)}
                  footer={[
                    <Button key="cerrar" onClick={() => setModalDetalleVisible(false)}>
                      Cerrar
                    </Button>
                  ]}
                                     width={900}
                  style={{ top: 20 }}
                >
                  <div style={{ padding: '20px 0' }}>
                    {fardosGrabados.length === 0 ? (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '40px',
                        color: '#666'
                      }}>
                        <Text style={{ fontSize: '16px' }}>
                          No hay fardos grabados aún
                        </Text>
                      </div>
                    ) : (
                      <div style={{ 
                        maxHeight: '60vh', 
                        overflowY: 'auto',
                        border: '1px solid #f0f0f0',
                        borderRadius: '8px'
                      }}>
                        <Table
                          dataSource={fardosGrabados}
                          columns={columns}
                          rowKey="id"
                          pagination={false}
                          size="small"
                          scroll={{ y: '50vh' }}
                          style={{
                            backgroundColor: '#ffffff'
                          }}
                        />
                      </div>
                    )}
                    
                                         {/* Resumen rápido en el modal de detalle */}
                     {fardosGrabados.length > 0 && (
                       <div style={{ 
                         marginTop: '20px',
                         padding: '16px',
                         backgroundColor: '#f8f9fa',
                         borderRadius: '8px',
                         border: '1px solid #e9ecef'
                       }}>
                         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                           <Text strong style={{ fontSize: '14px' }}>
                             Total Fardos Activos: {fardosGrabados.filter(f => f.activo).length}
                           </Text>
                           <Text strong style={{ fontSize: '14px' }}>
                             Total Kilos Activos: {fardosGrabados.filter(f => f.activo).reduce((total, fardo) => total + fardo.kilos, 0).toFixed(2)} kg
                           </Text>
                         </div>
                         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                           <Text style={{ fontSize: '12px', color: '#666' }}>
                             Total Fardos (incl. bajas): {fardosGrabados.length}
                           </Text>
                           <Text style={{ fontSize: '12px', color: '#666' }}>
                             Fardos dados de baja: {fardosGrabados.filter(f => !f.activo).length}
                           </Text>
                         </div>
                       </div>
                     )}
                  </div>
                                 </Modal>

                 {/* Modal para cambiar Número de Fardo */}
                 <Modal
                   title="Cambiar Número de Fardo"
                   open={modalFardoVisible}
                   onCancel={() => setModalFardoVisible(false)}
                   footer={[
                     <Button key="cancelar" onClick={() => setModalFardoVisible(false)}>
                       Cancelar
                     </Button>,
                     <Button 
                       key="cambiar" 
                       type="primary"
                       onClick={cambiarNumeroFardo}
                       style={{ backgroundColor: '#87CEEB', borderColor: '#87CEEB' }}
                     >
                       Cambiar
                     </Button>
                   ]}
                   width={400}
                 >
                   <div style={{ padding: '20px 0' }}>
                     <div style={{ marginBottom: '20px' }}>
                       <Text style={{ fontSize: '14px', color: '#666' }}>
                         Número de fardo actual: <Text strong>{fardoNumero}</Text>
                       </Text>
                     </div>
                     
                     <div style={{ marginBottom: '20px' }}>
                       <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                         Nuevo número de fardo:
                       </Text>
                       <InputNumber
                         value={nuevoNumeroFardo}
                         onChange={(value) => setNuevoNumeroFardo(value)}
                         style={{
                           width: '100%',
                           height: '40px',
                           fontSize: '16px'
                         }}
                         min={1}
                         placeholder="Ingrese el nuevo número"
                       />
                     </div>
                     
                     <div style={{ 
                       padding: '12px', 
                       backgroundColor: '#f6ffed', 
                       borderRadius: '6px',
                       border: '1px solid #b7eb8f'
                     }}>
                       <Text style={{ fontSize: '12px', color: '#52c41a' }}>
                         ⚠️ Nota: Este cambio solo afectará al próximo fardo a grabar. 
                         Los fardos ya grabados mantendrán su numeración original.
                       </Text>
                     </div>
                   </div>
                                   </Modal>

                  {/* Modal de Tickets/Rollos */}
                  <Modal
                    title={`Tickets y Rollos [${desmoteData?.producto || 'FIBRA'}]`}
                    open={modalTicketsRollosVisible}
                    onCancel={() => setModalTicketsRollosVisible(false)}
                    footer={[
                      <Button key="cerrar" onClick={() => setModalTicketsRollosVisible(false)}>
                        Cerrar
                      </Button>
                    ]}
                    width={800}
                    style={{ top: 20 }}
                  >
                    <div style={{ padding: '20px 0' }}>
                      {desmoteData?.tickets && desmoteData.tickets.length > 0 ? (
                        <div style={{ 
                          maxHeight: '60vh', 
                          overflowY: 'auto',
                          border: '1px solid #f0f0f0',
                          borderRadius: '8px'
                        }}>
                          <Table
                            dataSource={desmoteData.tickets}
                            columns={[
                              {
                                title: 'N° Ticket',
                                dataIndex: 'numero_ticket',
                                key: 'numero_ticket',
                                width: 120,
                                render: (text: number) => (
                                  <Text strong style={{ fontSize: '14px' }}>
                                    {text}
                                  </Text>
                                ),
                              },
                              {
                                title: 'Rollo',
                                dataIndex: 'rollo_identificacion',
                                key: 'rollo_identificacion',
                                width: 150,
                                render: (text: string) => (
                                  <Text style={{ fontSize: '14px' }}>
                                    {text || '-'}
                                  </Text>
                                ),
                              },
                              {
                                title: 'Cliente',
                                dataIndex: 'cliente',
                                key: 'cliente',
                                width: 250,
                                render: (text: string) => (
                                  <Text style={{ fontSize: '14px' }}>
                                    {text}
                                  </Text>
                                ),
                              },
                              {
                                title: 'Kilos',
                                dataIndex: 'kilos_netos',
                                key: 'kilos_netos',
                                width: 120,
                                render: (text: number) => (
                                  <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                                    {text.toFixed(2)} kg
                                  </Text>
                                ),
                              },
                            ]}
                            rowKey={(record, index) => `${record.numero_ticket}-${record.rollo_identificacion}-${index}`}
                            pagination={false}
                            size="small"
                            scroll={{ y: '50vh' }}
                            style={{
                              backgroundColor: '#ffffff'
                            }}
                          />
                        </div>
                      ) : (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '40px',
                          color: '#666'
                        }}>
                          <Text style={{ fontSize: '16px' }}>
                            No hay datos de tickets y rollos disponibles
                          </Text>
                        </div>
                      )}
                      
                      {/* Resumen de tickets y rollos */}
                      {desmoteData?.tickets && desmoteData.tickets.length > 0 && (
                        <div style={{ 
                          marginTop: '20px',
                          padding: '16px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #e9ecef'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <Text strong style={{ fontSize: '14px' }}>
                              Total Rollos: {desmoteData.tickets.length}
                            </Text>
                            <Text strong style={{ fontSize: '14px' }}>
                              Total Kilos: {desmoteData.tickets.reduce((total, ticket) => total + (ticket.kilos_netos || 0), 0).toFixed(2)} kg
                            </Text>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text style={{ fontSize: '12px', color: '#666' }}>
                              Ticket: {desmoteData.ticketBalanza}
                            </Text>
                            <Text style={{ fontSize: '12px', color: '#666' }}>
                              Cliente: {desmoteData.cliente}
                            </Text>
                          </div>
                        </div>
                      )}
                    </div>
                  </Modal>
      </div>
      </Card>
    </div>
  );
};

export default Desmote;
